import axios from 'axios';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

const portalAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Handle 401 errors by redirecting to portal login
// ONLY redirect if we're on a /portal/* route (not the login page itself)
portalAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const pathname = window.location.pathname;

      // Don't redirect if already on login page or password setup page
      // Password setup page is intentionally accessible without auth
      if (pathname.includes('/login') || pathname.includes('/set-password')) {
        return Promise.reject(error);
      }

      // Only redirect to portal login if we're on a portal route
      // This prevents the interceptor from hijacking non-portal pages
      if (pathname.startsWith('/portal')) {
        localStorage.removeItem('portal_user');
        window.location.href = '/portal/login';
        return Promise.reject(error);
      }

      // For non-portal routes, just reject - let the main API handle it
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const portalAuthAPI = {
  login: async (email: string, password: string) => {
    const response = await axios.post(`${API_BASE_URL}/portal/auth/login/`, {
      email,
      password,
    }, { withCredentials: true });
    return response.data;
  },
  logout: async () => {
    try {
      await portalAPI.post('/portal/auth/logout/');
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('portal_user');
  },
  getMe: () => portalAPI.get('/portal/auth/me/'),
  activateFarmhand: () => portalAPI.post('/portal/auth/activate-farmhand/'),
};

export const portalDashboardAPI = {
  getStats: () => portalAPI.get('/portal/dashboard/stats/'),
  getFarmHealthDashboard: () => portalAPI.get('/planner/portal/farm-health-dashboard/'),
};

export const farmerPartnerWaitlistAPI = {
  // Leave an email for the Farmer's Partner launch notification list.
  signup: (email: string, source: string = 'farmhand_landing') =>
    portalAPI.post('/portal/farmer-partner-waitlist/', { email, source }),
};

export const portalOrdersAPI = {
  getAll: (params?: { page?: number; page_size?: number; status?: string; search?: string }) =>
    portalAPI.get('/portal/orders/', { params }),
  getById: (id: number) => portalAPI.get(`/portal/orders/${id}/`),
  create: (data: any) => portalAPI.post('/portal/orders/', data),
  getTracking: (id: number) => portalAPI.get(`/portal/orders/${id}/tracking/`),
  // Order modification endpoints
  update: (id: number, data: any) => portalAPI.patch(`/portal/orders/${id}/update_order/`, data),
  cancel: (id: number, reason?: string) => portalAPI.post(`/portal/orders/${id}/cancel_order/`, { reason: reason || '' }),
  // Order item management
  addItem: (orderId: number, data: any) => portalAPI.post(`/portal/orders/${orderId}/items/`, data),
  updateItem: (orderId: number, itemId: number, data: any) =>
    portalAPI.patch(`/portal/orders/${orderId}/items/${itemId}/`, data),
  removeItem: (orderId: number, itemId: number) =>
    portalAPI.delete(`/portal/orders/${orderId}/items/${itemId}/`),
};

export const portalQuotesAPI = {
  getAll: () => portalAPI.get('/portal/quotes/'),
  getById: (id: number) => portalAPI.get(`/portal/quotes/${id}/`),
  accept: (id: number, data: { delivery_date: string; shipping_method?: string; customer_po_number?: string }) =>
    portalAPI.post(`/portal/quotes/${id}/accept/`, data),
  decline: (id: number) => portalAPI.post(`/portal/quotes/${id}/decline/`),
};

export const portalFeedsAPI = {
  getAll: () => portalAPI.get('/portal/available-feeds/'),
  getFilterOptions: (params?: { feed_grade?: string; animal_type?: string }) =>
    portalAPI.get('/portal/available-feeds/filter_options/', { params }),
};

export const portalProductsAPI = {
  search: (query: string) => portalAPI.get('/portal/products/search/', { params: { q: query } }),
  getRecent: () => portalAPI.get('/portal/products/recent/'),
  parsePurchaseOrder: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }).post('/portal/parse-purchase-order/', formData);
  },
};

export const taxCertificatesAPI = {
  getAll: () => portalAPI.get('/tax-certificates/'),
  getById: (id: number) => portalAPI.get(`/tax-certificates/${id}/`),
  upload: (formData: FormData) => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }).post('/tax-certificates/', formData);
  },
  supersede: (id: number) => portalAPI.post(`/tax-certificates/${id}/supersede/`),
  download: (id: number) => portalAPI.get(`/tax-certificates/${id}/download/`),
  generateCertificate: (data: {
    account_id?: string;
    entity_type: 'individual' | 'corporation' | 'llc' | 'partnership' | 'other';
    entity_type_other?: string;
    buyer_name: string;
    farm_name: string;
    mailing_address: string;
    city: string;
    state: string;
    zip: string;
    title: string;
    phone?: string;
    signature_image?: string;
  }) => {
    return axios.create({
      baseURL: API_BASE_URL,
      responseType: 'blob',
      withCredentials: true,
    }).post('/portal/tax-exemption/generate-certificate/', data);
  },
};

export const taxDocumentsAPI = {
  getAll: () => portalAPI.get('/portal/tax-documents/'),
  getStatus: () => portalAPI.get('/portal/tax-documents/status/'),
  upload: (formData: FormData) => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }).post('/portal/tax-documents/', formData);
  },
  delete: (id: number) => portalAPI.delete(`/portal/tax-documents/${id}/`),
  download: (id: number) => portalAPI.get(`/portal/tax-documents/${id}/download/`),
};

export const portalReportsAPI = {
  getOrderHistory: (params?: { year?: number; search?: string; page?: number; per_page?: number }) =>
    portalAPI.get('/portal/reports/order_history/', { params }),
  getAnnualReport: (params?: { year?: number }) =>
    portalAPI.get('/portal/reports/annual_report/', { params }),
  getReorderSuggestions: () =>
    portalAPI.get('/portal/reports/reorder_suggestions/'),
  exportPDF: (params?: { year?: number; type?: string }) => {
    return axios.create({
      baseURL: API_BASE_URL,
      responseType: 'blob',
      withCredentials: true,
    }).get('/portal/reports/export_pdf/', { params });
  },
};

export const portalEmailPreferencesAPI = {
  get: () => portalAPI.get('/portal/email-preferences/'),
  update: (preferences: any) => portalAPI.put('/portal/email-preferences/update_preferences/', preferences),
};

export const portalShippingPreferencesAPI = {
  get: () => portalAPI.get('/portal/shipping-preferences/'),
  update: (preferences: any) => portalAPI.patch('/portal/shipping-preferences/', preferences),
};

export const portalDeliveryScheduleAPI = {
  get: () => portalAPI.get('/portal/delivery-schedule/'),
  getValidDates: () => portalAPI.get('/portal/valid-delivery-dates/'),
};

export const portalShippingEstimateAPI = {
  estimate: (data: {
    items: Array<{ formula_id: number; quantity_lbs: number; num_packages?: number; package_type?: string }>;
    shipping_method: string;
    liftgate?: boolean;
  }) => portalAPI.post('/portal/estimate-shipping/', data),
};

export const portalMilkRunAPI = {
  getInfo: () => portalAPI.get('/portal/milk-run-info/'),
};

export const portalTeamAPI = {
  listMembers: () => portalAPI.get('/portal/team/list_members/'),
  addMember: (data: { email: string; first_name: string; last_name: string; role: string; send_invite?: boolean }) =>
    portalAPI.post('/portal/team/add_member/', data),
  updateMember: (data: { member_id: number; role?: string; permissions?: Record<string, boolean> }) =>
    portalAPI.post('/portal/team/update_member/', data),
  removeMember: (memberId: number) =>
    portalAPI.post('/portal/team/remove_member/', { member_id: memberId }),
  resendInvite: (memberId: number) =>
    portalAPI.post('/portal/team/resend_invite/', { member_id: memberId }),
};

export const portalPromotionsAPI = {
  getAll: () => portalAPI.get('/portal/bridge-club/promotions/'),
  create: (data: FormData) => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }).post('/portal/bridge-club/create_promotion/', data);
  },
  update: (id: number, data: FormData) => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }).patch(`/portal/bridge-club/${id}/update_promotion/`, data);
  },
  delete: (id: number) => portalAPI.delete(`/portal/bridge-club/${id}/delete_promotion/`),
};

// Bridge Club Portal API (for retail partners)
export const portalBridgeClubAPI = {
  // Get Bridge Club analytics for this store
  getAnalytics: () => portalAPI.get('/portal/bridge-club/analytics/'),
  // Get QR code placement analytics for this store
  getQRAnalytics: () => portalAPI.get('/portal/bridge-club/qr-analytics/'),
};

// Farm Team Management (for wholesale customers)
export const farmTeamAPI = {
  listMembers: () => portalAPI.get('/portal/farm-team/list_members/'),
  addMember: (data: { email: string; first_name: string; last_name: string; farm_role: string; send_invite?: boolean }) =>
    portalAPI.post('/portal/farm-team/add_member/', data),
  updateMember: (data: { member_id: number; farm_role?: string; permissions?: Record<string, boolean> }) =>
    portalAPI.post('/portal/farm-team/update_member/', data),
  removeMember: (memberId: number) =>
    portalAPI.post('/portal/farm-team/remove_member/', { member_id: memberId }),
  resendInvite: (memberId: number) =>
    portalAPI.post('/portal/farm-team/resend_invite/', { member_id: memberId }),
};

// Farm Tasks Management
export const farmTasksAPI = {
  getAll: (params?: { status?: string; assigned_to?: string | number; priority?: string }) =>
    portalAPI.get('/portal/farm-tasks/', { params }),
  getById: (id: number) => portalAPI.get(`/portal/farm-tasks/${id}/`),
  create: (data: {
    title: string;
    description?: string;
    due_date?: string;
    priority?: string;
    category?: string;
    assigned_to?: number;
    zone_id?: number;
    batch_id?: string;
  }) => portalAPI.post('/portal/farm-tasks/', data),
  update: (id: number, data: Partial<{
    title: string;
    description: string;
    due_date: string | null;
    priority: string;
    category: string;
    status: string;
    assigned_to: number | null;
    zone_id: number | null;
  }>) => portalAPI.patch(`/portal/farm-tasks/${id}/`, data),
  complete: (id: number) => portalAPI.post(`/portal/farm-tasks/${id}/complete/`),
  delete: (id: number) => portalAPI.delete(`/portal/farm-tasks/${id}/`),
  getActivity: (id: number) => portalAPI.get(`/portal/farm-tasks/${id}/activity/`),
};

// Batch Notes API - Voice notes, photos, and predator tracking
export interface BatchNote {
  id: number;
  batch_id: number | null;
  batch_name: string | null;
  task_id: number | null;
  zone_id: number | null;
  note_text: string;
  note_type: string;
  note_type_display: string;
  structured_data: Record<string, unknown>;
  latitude: number | null;
  longitude: number | null;
  include_in_report: boolean;
  created_by: { id: number | null; name: string | null };
  photos: Array<{ id: number; url: string; caption: string; uploaded_at: string }>;
  created_at: string;
  updated_at?: string;
  predator_details?: {
    predator_type: string;
    predator_type_display: string;
    predator_confirmed: boolean;
    losses: number;
    evidence_observed: string[];
    incident_time: string;
    incident_time_display: string;
    identification_method: string;
  };
}

export interface VoiceNoteExtractedData {
  note_text: string;
  note_type: string;
  detected_batch: { id: number | null; mentioned_name: string | null } | null;
  detected_zone: { id: number | null; mentioned_name: string | null } | null;
  structured_data: Record<string, unknown>;
  is_predator_incident: boolean;
  predator_data: {
    predator_type: string;
    predator_confirmed: boolean;
    time_of_day: string;
    evidence: string[];
    losses: number;
  } | null;
  confidence: 'high' | 'medium' | 'low';
  suggestions: string[];
}

export interface VoiceNoteProcessingResult {
  success: boolean;
  transcription: string;
  duration: number | null;
  extracted: VoiceNoteExtractedData;
  available_batches: Array<{ id: number; name: string; species: string; quantity: number }>;
  available_zones: Array<{ id: number; name: string; zone_type: string }>;
}

export interface PredatorSuggestion {
  predator_type: string;
  confidence: 'high' | 'medium' | 'low';
  confidence_score: number;
  label: string;
}

export interface PredatorIdentificationResult {
  success: boolean;
  suggestions: PredatorSuggestion[];
  confidence_notes: string[];
  evidence_count: number;
  time_of_day: string;
}

export interface PredatorSummary {
  total_incidents: number;
  total_losses: number;
  recent_incidents_90d: number;
  recent_losses_90d: number;
  most_common_predator: string | null;
  predator_stats: Record<string, { count: number; total_losses: number; last_incident: string | null }>;
  high_risk_zones: Array<{ zone_id: number; incidents: number; top_predator: string | null }>;
}

export const batchNotesAPI = {
  // List batch notes with optional filters
  getAll: (params?: {
    batch_id?: number;
    note_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) => portalAPI.get<{ notes: BatchNote[] }>('/portal/batch-notes/', { params }),

  // Get single note detail
  getById: (id: number) => portalAPI.get<BatchNote>(`/portal/batch-notes/${id}/`),

  // Create a new note (supports FormData for photo uploads)
  create: (data: FormData | {
    note_text: string;
    note_type?: string;
    batch_id?: number;
    task_id?: number;
    zone_id?: number;
    structured_data?: Record<string, unknown>;
    latitude?: number;
    longitude?: number;
    include_in_report?: boolean;
  }) => {
    if (data instanceof FormData) {
      return portalAPI.post('/portal/batch-notes/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return portalAPI.post('/portal/batch-notes/', data);
  },

  // Update a note
  update: (id: number, data: Partial<{
    note_text: string;
    note_type: string;
    structured_data: Record<string, unknown>;
    include_in_report: boolean;
    batch_id: number | null;
    zone_id: number | null;
  }>) => portalAPI.patch(`/portal/batch-notes/${id}/`, data),

  // Delete a note
  delete: (id: number) => portalAPI.delete(`/portal/batch-notes/${id}/`),

  // Add photo to existing note
  addPhoto: (noteId: number, photo: File, caption?: string) => {
    const formData = new FormData();
    formData.append('photo', photo);
    if (caption) formData.append('caption', caption);
    return portalAPI.post(`/portal/batch-notes/${noteId}/add_photo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Remove photo from note
  removePhoto: (noteId: number, photoId: number) =>
    portalAPI.delete(`/portal/batch-notes/${noteId}/remove_photo/${photoId}/`),

  // Process voice note - transcribe and extract data
  processVoiceNote: (audioFile: File) => {
    const formData = new FormData();
    formData.append('audio', audioFile);
    return portalAPI.post<VoiceNoteProcessingResult>('/portal/batch-notes/process_voice_note/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get predator identification suggestions
  identifyPredator: (data: {
    evidence: string[];
    time_of_day?: string;
    zone_id?: number;
    losses?: number;
  }) => portalAPI.post<PredatorIdentificationResult>('/portal/batch-notes/identify_predator/', data),

  // Get evidence options for predator ID (includes available batches and zones)
  getEvidenceOptions: () =>
    portalAPI.get<{
      categories: Record<string, Array<[string, string]>>;
      available_batches: Array<{ id: number; name: string; species: string; quantity: number }>;
      available_zones: Array<{ id: number; name: string; zone_type: string }>;
    }>('/portal/batch-notes/evidence_options/'),

  // Get farm predator summary
  getPredatorSummary: () =>
    portalAPI.get<PredatorSummary>('/portal/batch-notes/predator_summary/'),

  // Create note with predator incident
  createWithPredator: (data: FormData | {
    note_text: string;
    batch_id?: number;
    zone_id?: number;
    structured_data?: Record<string, unknown>;
    latitude?: number;
    longitude?: number;
    predator_data: {
      predator_type: string;
      predator_confirmed: boolean;
      losses: number;
      evidence: string[];
      time_of_day: string;
      identification_method?: string;
    };
  }) => {
    if (data instanceof FormData) {
      return portalAPI.post('/portal/batch-notes/create_with_predator/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return portalAPI.post('/portal/batch-notes/create_with_predator/', data);
  },
};

// Tutorial Progress API
export const tutorialProgressAPI = {
  // Get current user's tutorial progress
  get: () => portalAPI.get('/portal/tutorial-progress/'),

  // Mark a tutorial as completed
  complete: (pageKey: string, dismissed: boolean = false) =>
    portalAPI.post('/portal/tutorial-progress/complete/', { pageKey, dismissed }),

  // Reset a single tutorial so it shows again
  reset: (pageKey: string) =>
    portalAPI.post('/portal/tutorial-progress/reset/', { pageKey }),

  // Reset all tutorials (for testing/support)
  resetAll: () =>
    portalAPI.post('/portal/tutorial-progress/reset_all/'),
};

// Support Tickets API
export const supportTicketsAPI = {
  getAll: () => portalAPI.get('/portal/support/tickets/'),
  getById: (id: number) => portalAPI.get(`/portal/support/tickets/${id}/`),
  create: (data: FormData) =>
    portalAPI.post('/portal/support/tickets/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  addMessage: (ticketId: number, message: string) =>
    portalAPI.post(`/portal/support/tickets/${ticketId}/messages/`, { message }),
  addAttachment: (ticketId: number, data: FormData) =>
    portalAPI.post(`/portal/support/tickets/${ticketId}/attachments/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export default portalAPI;
